import { useState } from "react";
import Growth from "@/assets/icons/Growth.svg";
import { CustomButton } from "@/components/ui/Buttons";
import { Link, useParams } from "react-router-dom";
import {
  ProductStructureCardProps,
  TProductStructureCard,
} from "./ProductStructureType";
import GenerateScriptSkeleton from "./GenerateScriptSkeleton";
import GenerateScripts from "./GenerateScripts";
import SimilarStructure from "./SimilarStructure";

const ProductStructureCard = ({
  data,
  index,
  currentlyPlaying,
  setCurrentlyPlaying,
  setSelectedProductId,
  isSelected = false,
  isGeneratingScript = false,
  isScriptReady = false,
  isError,
  generatedScripts = [],
  setIsSelectModalOpen,
  isAnyScriptGenerating = false,
  shouldShowMarketScriptBanner,
}: ProductStructureCardProps) => {
  const { brandName, productName } = useParams();

  const scriptData = [
    {
      id: "hook",
      description: data?.hook?.[0] || "Hook content not available",
    },
    {
      id: "body",
      description: data?.body?.[0] || "Body content not available",
    },
    {
      id: "cta",
      description: data?.cta?.[0] || "CTA content not available",
    },
  ];

  const [scriptDataType, setScriptDataType] = useState<{
    id: string;
    description: string;
  }>(scriptData[0]);

  // Handle script generation
  const handleGenerateScript = () => {
    // Prevent generation if any script is already generating and this card is not the selected one
    if (isAnyScriptGenerating && !isSelected) {
      return;
    }

    if (setSelectedProductId) {
      setSelectedProductId(data.id);
    }
    setScriptDataType({
      id: "generateScript",
      description: "Generating script...",
    });
  };

  // Show the appropriate content based on selection
  const getDisplayContent = () => {
    if (scriptDataType.id === "generateScript") {
      if (isSelected && isGeneratingScript) {
        return "✨ Generating your personalized script...";
      } else if (
        isSelected &&
        isScriptReady &&
        !isError &&
        generatedScripts.length > 0
      ) {
        return "🎉 Script generated successfully! ";
      } else if (isSelected && isError) {
        return "❌ Failed to generate script. Please try again.";
      } else if (!isSelected) {
        return "Click 'Generate Script' to create script.";
      } else {
        return "Preparing to generate script...";
      }
    }
    return scriptDataType.description;
  };

  // Get the appropriate styling for the generate script button and text
  const getGenerateScriptStyling = () => {
    // If another card is generating and this is not the selected card, show disabled state
    if (isAnyScriptGenerating && !isSelected) {
      return {
        buttonClass: "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50",
        textClass: "text-gray-500",
      };
    }

    if (isSelected && isGeneratingScript) {
      return {
        buttonClass:
          "bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-wait",
        textClass: "text-blue-600 font-medium animate-pulse",
      };
    } else if (
      isSelected &&
      isScriptReady &&
      !isError &&
      generatedScripts.length > 0
    ) {
      return {
        buttonClass: "bg-gradient-to-r from-green-500 to-green-600 text-white",
        textClass: "text-green-600 font-medium",
      };
    } else if (isSelected && isError) {
      return {
        buttonClass: "bg-gradient-to-r from-red-500 to-red-600 text-white",
        textClass: "text-red-600 font-medium",
      };
    } else {
      return {
        buttonClass:
          "bg-[#F2F4F7] text-[#788193] hover:bg-[#E5E7EB] hover:text-[#374151]",
        textClass: "text-[#0D0D0D] font-medium",
      };
    }
  };

  // Determine what content to show
  const renderContentSection = () => {
    // Show skeleton loader when this specific card is generating script
    if (
      scriptDataType.id === "generateScript" &&
      isSelected &&
      isGeneratingScript
    ) {
      return <GenerateScriptSkeleton />;
    }

    // Show generated scripts carousel when ready for this specific card
    if (
      scriptDataType.id === "generateScript" &&
      isSelected &&
      isScriptReady &&
      !isError &&
      generatedScripts.length > 0
    ) {
      return <GenerateScripts generatedScripts={generatedScripts} />;
    }

    // Default: show similar structure videos
    return (
      <SimilarStructure
        data={data as TProductStructureCard}
        currentlyPlaying={currentlyPlaying}
        setCurrentlyPlaying={setCurrentlyPlaying}
      />
    );
  };

  const styling = getGenerateScriptStyling();

  // Check if the generate script button should be disabled
  const isGenerateButtonDisabled =
    (isAnyScriptGenerating && !isSelected) || shouldShowMarketScriptBanner;

  return (
    <>
      <div
        className={`structure-card-shadow py-4 px-5 rounded-[20px] h-[545px] overflow-scroll max-w-[444px] transition-all duration-300 ${
          isSelected
            ? "ring-2 ring-blue-500 bg-blue-50 shadow-lg"
            : "hover:shadow-md"
        } ${isGenerateButtonDisabled ? "opacity-75" : ""}`}
      >
        {/* card header */}
        <div>
          <h6 className="text-[#0D0D0D] font-semibold text-xl leading-6 tracking-normal mb-5">
            {data.ad_format &&
              data.ad_format
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}
          </h6>

          {data.engagement_metric && (
            <div className="bg-[#E2FFE3] rounded-[10px] py-1.5 px-2 inline-flex items-center text-xs font-medium">
              <Growth />
              <span className="text-[#0B8000] text-[13px] leading-[125%] tracking-[-0.75%] font-semibold">
                {Math.round(data.engagement_metric)}% engagement
              </span>
            </div>
          )}
        </div>

        {/* Hook body cta */}
        <div className="my-5">
          <div className="flex justify-between gap-x-2">
            <div className="flex gap-x-2 flex-wrap">
              {scriptData.map((item) => (
                <div
                  key={item.id}
                  onClick={() =>
                    !isGenerateButtonDisabled &&
                    setScriptDataType({
                      id: item.id,
                      description: item.description,
                    })
                  }
                  className={`cursor-pointer py-1 px-2 rounded-[4px] transition-all duration-200 hover:scale-105
                ${
                  scriptDataType.id === item.id
                    ? "bg-[#1B1B1B] text-white shadow-md"
                    : "bg-[#F2F4F7] text-[#788193] hover:bg-[#E5E7EB] hover:text-[#374151]"
                }
                ${
                  isGenerateButtonDisabled
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
                `}
                >
                  <p className="capitalize text-sm font-medium leading-5 tracking-[-2.5%]">
                    {item.id}
                  </p>
                </div>
              ))}
            </div>

            <div
              onClick={handleGenerateScript}
              className={`py-1 px-2 rounded-[4px] transition-all duration-200 ${
                !isGenerateButtonDisabled
                  ? "cursor-pointer hover:scale-105"
                  : "cursor-not-allowed"
              } ${styling.buttonClass}
              ${
                scriptDataType.id === "generateScript"
                  ? "ring-1 ring-offset-1 ring-current"
                  : ""
              }
              `}
            >
              <p className="capitalize text-sm font-medium leading-5 tracking-[-2.5%]">
                {isGenerateButtonDisabled && !isSelected ? (
                  <span className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Wait...
                  </span>
                ) : isSelected && isGeneratingScript ? (
                  <span className="flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Generating...
                  </span>
                ) : isSelected &&
                  isScriptReady &&
                  !isError &&
                  generatedScripts.length > 0 ? (
                  <span className="flex items-center gap-1">✓ Generated</span>
                ) : isSelected && isError ? (
                  <span className="flex items-center gap-1">⚠ Retry</span>
                ) : (
                  "Generate Script"
                )}
              </p>
            </div>
          </div>

          <div className="mt-3 p-2 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
            <p
              className={`text-sm leading-6 tracking-[0%] ${styling.textClass}`}
            >
              {getDisplayContent()}
            </p>
          </div>
        </div>

        {/* Conditional Rendering: Skeleton, Scripts, or Similar Structure */}
        {renderContentSection()}

        {/* Action button */}
        <CustomButton
          onClick={() => setIsSelectModalOpen(true)}
          variant="outline"
          border="2px solid #171717"
          padding="12px"
          fontWeight="600"
          color="#171717"
          fontSize="20px"
          borderRadius="12px"
          className="w-full leading-6 tracking-[0%] hover:bg-[#171717] hover:text-white transition-all duration-200 mt-auto"
        >
          Select
        </CustomButton>
      </div>
    </>
  );
};

export default ProductStructureCard;
